<template>
  <div class="p-6 bg-[#F8F9FB] min-h-screen font-inter text-bodyText">
    <div class="max-w-[1240px] mx-auto">
      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center py-40"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryDark mb-4"
        ></div>
        <p class="text-grayDark font-medium">Fetching expert profile...</p>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center py-40 text-center"
      >
        <div class="text-red-500 mb-4 text-4xl">⚠️</div>
        <h3 class="text-xl font-bold text-primaryDark mb-2">{{ error }}</h3>
        <button @click="goBack" class="text-primaryDark underline font-medium">
          Return to Talent Pool
        </button>
      </div>

      <div v-else>
        <!-- Top Header Action Bar (Unified Alignment) -->
        <div class="flex justify-between items-end mb-8 px-1">
          <!-- Close Button -->
          <button
            @click="goBack"
            class="flex items-center gap-2 px-6 py-2 bg-white border border-grayLight rounded-lg text-[13px] font-semibold text-grayDark hover:bg-gray-50 transition-colors shadow-sm"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Close
          </button>

          <!-- Profile Badge -->
          <div class="inline-flex">
            <span
              class="px-4 py-1.5 bg-white border border-grayLight rounded-lg text-[13px] font-semibold text-primaryDark shadow-sm"
            >
              Profile
            </span>
          </div>
        </div>

        <div class="flex flex-col lg:flex-row gap-10">
          <!-- Left Sidebar (Identity & Metrics) -->
          <div class="w-full lg:w-[360px] space-y-10">

          <!-- Identity Card -->
          <div
            class="bg-white border border-grayLight rounded-2xl overflow-hidden shadow-sm"
          >
            <div
              class="relative h-[280px] bg-white flex items-center justify-center overflow-hidden"
            >
              <!-- Concentric Dotted Pattern -->
              <svg
                class="absolute inset-0 w-full h-full opacity-[0.15]"
                viewBox="0 0 360 280"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="180"
                  cy="140"
                  r="120"
                  stroke="#ABABB3"
                  stroke-width="1"
                  stroke-dasharray="2 6"
                />
                <circle
                  cx="180"
                  cy="140"
                  r="100"
                  stroke="#ABABB3"
                  stroke-width="1"
                  stroke-dasharray="2 6"
                />
                <circle
                  cx="180"
                  cy="140"
                  r="80"
                  stroke="#ABABB3"
                  stroke-width="1"
                  stroke-dasharray="2 6"
                />
                <circle
                  cx="180"
                  cy="140"
                  r="60"
                  stroke="#ABABB3"
                  stroke-width="1"
                  stroke-dasharray="2 6"
                />
                <circle
                  cx="180"
                  cy="140"
                  r="40"
                  stroke="#ABABB3"
                  stroke-width="1"
                  stroke-dasharray="2 6"
                />
                <circle
                  cx="180"
                  cy="140"
                  r="20"
                  stroke="#ABABB3"
                  stroke-width="1"
                  stroke-dasharray="2 6"
                />
              </svg>
              <!-- Profile Initials -->
              <div
                class="w-24 h-24 bg-[#E5E7ED] rounded-full flex items-center justify-center text-primaryDark text-[28px] font-semibold relative z-10 shadow-sm border-4 border-white"
              >
                {{ getInitials(talent.name) }}
              </div>
            </div>
            <div class="p-8 text-center border-t border-gray-50">
              <div class="flex items-center justify-center gap-2 mb-2">
                <h2
                  class="text-[22px] font-bold text-primaryDark leading-tight"
                >
                  {{ talent.name }}
                </h2>
                <span
                  class="px-2 py-0.5 bg-[#F3F4F6] text-[#73488D] text-[9px] font-bold rounded uppercase border border-[#73488D]"
                >
                  {{ talent.role }}
                </span>
              </div>
              <div
                class="flex items-center justify-center gap-3 mt-3 text-[13px]"
              >
                <div
                  class="flex items-center gap-1.5 text-[#14C100] font-semibold"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-[#14C100]"></span>
                  {{ talent.availability }}
                </div>
                <span class="text-gray-200 text-[16px]">•</span>
                <div class="text-grayDark font-medium">
                  {{ talent.location }}
                </div>
              </div>
            </div>
          </div>

          <!-- Metrics Card -->
          <div
            class="bg-white border border-grayLight rounded-2xl p-8 space-y-10 shadow-sm"
          >
            <!-- Skill Match -->
            <div>
              <div class="flex justify-between items-center mb-4">
                <span
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-widest"
                  >Skill Match</span
                >
                <span
                  class="text-[10px] font-bold text-primaryDark px-1.5 py-0.5 border border-gray-100 rounded"
                  >{{ talent.skillMatch }}%</span
                >
              </div>
              <div class="h-[3px] bg-gray-100 rounded-full overflow-hidden">
                <div
                  class="h-full bg-primaryDark transition-all duration-1000"
                  :style="{ width: talent.skillMatch + '%' }"
                ></div>
              </div>
            </div>

            <!-- Client Satisfaction -->
            <div>
              <span
                class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6 block"
                >Client Satisfaction/YOE</span
              >
              <div class="flex items-end gap-2 mb-4">
                <span class="text-[18px] font-bold text-primaryDark">{{
                  talent.rating
                }}</span>
                <span class="text-[11px] text-gray-400 mb-0.5 ml-2"
                  >/ {{ talent.yearsExperience }} years</span
                >
              </div>
              <!-- Sparkline simulation -->
              <div class="w-full h-12 mt-4 pr-4">
                <svg
                  viewBox="0 0 200 40"
                  class="w-full h-full"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 35 L20 32 L40 33 L60 20 L80 25 L100 15 L120 22 L140 10 L160 8 L180 5 L200 2"
                    fill="none"
                    stroke="#22C55E"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Content Area -->
        <div class="flex-grow space-y-6">
          <!-- Professional Summary -->
          <div
            class="bg-white border border-grayLight rounded-2xl p-10 shadow-sm"
          >
            <h3 class="text-[16px] font-bold text-bodyText mb-6">
              Professional Summary
            </h3>
            <div class="flex gap-6">
              <!-- Vertical Line -->
              <div
                class="w-[3px] bg-[#73488D] bg-opacity-30 rounded-full self-stretch"
              ></div>

              <div class="flex flex-col gap-5">
                <div class="flex items-center gap-3">
                  <!-- Verified Icon: Info icon -->
                  <div
                    class="w-5 h-5 bg-[#73488D] rounded-full flex items-center justify-center"
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                        stroke="white"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <p class="text-[11px] text-[#73488D] italic">
                    This talent profile is verified
                  </p>
                </div>

                <div
                  class="text-[12px] leading-relaxed text-[#4A4E57] space-y-4 pr-10"
                >
                  <p>{{ talent.summary }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Skills Matrix -->
          <div
            class="bg-white border border-grayLight rounded-2xl p-10 shadow-sm"
          >
            <h3 class="text-[16px] font-bold text-bodyText mb-8">
              Skills & Proficiency Matrix
            </h3>
            <div
              v-if="talent.skills.length > 0"
              class="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8"
            >
              <div v-for="(skill, index) in talent.skills" :key="index">
                <div class="flex justify-between items-center mb-3">
                  <span class="text-[14px] font-medium text-[#4A4E57]">{{
                    skill.name
                  }}</span>
                  <span
                    class="text-[12px] font-bold text-primaryDark px-1 py-0.5 border border-gray-100 rounded"
                    >{{ skill.value }}%</span
                  >
                </div>
                <div class="h-[3px] bg-gray-50 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-primaryDark"
                    :style="{ width: skill.value + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            <p v-else class="text-[13px] text-grayDark italic">
              No specialized skills listed.
            </p>
          </div>

          <!-- Work History -->
          <div
            class="bg-white border border-grayLight rounded-2xl p-10 shadow-sm"
          >
            <h3 class="text-[16px] font-bold text-bodyText mb-8">
              Work History & Highlights
            </h3>
            <div
              v-if="talent.workHistory.length > 0"
              class="space-y-0 relative ml-2"
            >
              <div
                class="absolute left-[2.5px] top-1 bottom-6 w-[1px] bg-gray-100"
              ></div>
              <div
                v-for="(item, index) in talent.workHistory"
                :key="index"
                class="relative pl-8 pb-10 last:pb-0"
              >
                <div
                  class="absolute left-[-1px] top-1.5 w-[8px] h-[8px] rounded-full bg-gray-400 ring-2 ring-white"
                ></div>
                <span
                  class="text-[12px] font-bold text-gray-400 mb-1.5 block uppercase"
                  >{{ item.year }}</span
                >
                <p class="text-[14px] text-[#4A4E57] leading-relaxed">
                  {{ item.description }}
                </p>
              </div>
            </div>
            <p v-else class="text-[13px] text-grayDark italic">
              No work history highlights found.
            </p>
          </div>

          <!-- Market Insights -->
          <div
            class="bg-white border border-grayLight rounded-2xl p-10 shadow-sm"
          >
            <h3 class="text-[16px] font-bold text-bodyText mb-8">
              Market Insights
            </h3>
            <div
              v-if="talent.marketInsights.length > 0"
              class="space-y-0 relative ml-2"
            >
              <div
                class="absolute left-[2.5px] top-1 bottom-6 w-[1px] bg-gray-100"
              ></div>
              <div
                v-for="(item, index) in talent.marketInsights"
                :key="index"
                class="relative pl-8 pb-10 last:pb-0"
              >
                <div
                  class="absolute left-[-1px] top-1.5 w-[8px] h-[8px] rounded-full bg-gray-300 ring-2 ring-white"
                ></div>
                <span
                  class="text-[12px] font-bold text-gray-400 mb-1.5 block uppercase"
                  >{{ item.year }}</span
                >
                <p class="text-[14px] text-[#4A4E57] leading-relaxed">
                  {{ item.description }}
                </p>
              </div>
            </div>
            <p v-else class="text-[13px] text-grayDark italic">
              No market insights available at this time.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import apiClient from "@/axios";

export default {
  name: "TalentProfileComponent",
  data() {
    return {
      talent: {
        name: "",
        role: "Professional",
        availability: "Available",
        location: "N/A",
        skillMatch: 0,
        rating: "0.0/5.0",
        summary: "No summary provided.",
        skills: [],
        workHistory: [],
        marketInsights: [],
        yearsExperience: 0,
      },
      loading: true,
      error: null,
    };
  },
  mounted() {
    this.fetchTalentDetail();
  },
  methods: {
    async fetchTalentDetail() {
      this.loading = true;
      this.error = null;
      try {
        const talentId = this.$route.params.id;
        const response = await apiClient.get(`/general/b2b/verified/talents/${talentId}`);
        const data = response.data.data;

        if (data) {
          this.talent = {
            name:
              `${data.user?.first_name || ""} ${
                data.user?.last_name || ""
              }`.trim() || "Expert Talent",
            role: data.position || "Expert Professional",
            availability: data.availability || "Available",
            location: data.location || "N/A",
            skillMatch: Math.round(
              (data.profile_completion_score || 0.9) * 100,
            ),
            rating:
              data.rating?.overall ||
              `${(data.client_satisfaction || 4.75).toFixed(2)}/5.0`,
            summary: data.user?.bio || "No summary provided.",
            skills: (data.skills || []).map((s) => ({
              name: s.skill_name,
              value: 90,
            })),
            yearsExperience: data.experience_depth || 0,
            workHistory: this.formatListData(data.achievement),
            marketInsights: this.formatListData(data.milestone),
          };
        } else {
          this.error = "Talent profile data not found.";
        }
      } catch (err) {
        console.error("Error fetching talent detail:", err);
        this.error = "Failed to load expert profile. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    formatListData(data) {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      return Object.entries(data).map(([key, val]) => {
        const readableYear = key.replace(/_/g, " ").toUpperCase();
        return {
          year: readableYear,
          description: typeof val === "object" ? JSON.stringify(val) : val,
        };
      });
    },
    getInitials(name) {
      if (!name) return "TI";
      const parts = name.split(" ").filter((p) => p.trim() !== "");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      } else if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
      return "TI";
    },
    goBack() {
      this.$router.push("/talent-pool");
    },
  },
};
</script>

<style scoped>
.font-inter {
  font-family: "Inter", sans-serif;
}
</style>
